import Vue, { VueConstructor } from "vue";
import {
  ApolloQueryResult,
  ObservableQuery,
  WatchQueryFetchPolicy,
} from "@apollo/client/core";
import { DocumentNode } from "graphql";

import { apolloClient } from "@/apollo";

type Variables = Record<string, unknown>;
type OptionalVariables = Variables | null | undefined;

type MaybeFactory<T> = T | ((this: any) => T);

export type LegacySmartQueryDefinition = {
  query: DocumentNode;
  variables?: MaybeFactory<OptionalVariables>;
  skip?: MaybeFactory<boolean>;
  fetchPolicy?: WatchQueryFetchPolicy;
  notifyOnNetworkStatusChange?: boolean;
  deep?: boolean;
  update?: (this: any, data: any) => unknown;
};

type LegacySmartQueryDefinitions = Record<string, LegacySmartQueryDefinition>;

type SubscriptionLike = {
  unsubscribe: () => void;
};

class LegacySmartQuery {
  private observable: ObservableQuery<any, any> | null = null;
  private subscription: SubscriptionLike | null = null;
  private skipUnwatch: (() => void) | null = null;
  private variablesUnwatch: (() => void) | null = null;
  private active = false;
  private loading = false;
  private previousVariablesKey: string | null = null;
  private readonly hasDataField: boolean;

  constructor(
    private readonly vm: Vue,
    private readonly key: string,
    private readonly definition: LegacySmartQueryDefinition,
    private readonly setLoading: (key: string, loading: boolean) => void
  ) {
    this.hasDataField = Object.prototype.hasOwnProperty.call(
      this.vm.$data,
      this.key
    );
  }

  start() {
    this.skipUnwatch = this.vm.$watch(
      () => this.shouldSkip(),
      (skip) => this.setSkip(skip),
      { immediate: true, deep: this.definition.deep }
    );
  }

  stop() {
    this.skipUnwatch?.();
    this.skipUnwatch = null;
    this.stopActiveQuery();
  }

  async refetch(
    variables?: OptionalVariables
  ): Promise<ApolloQueryResult<any> | null> {
    if (!this.observable) {
      if (this.shouldSkip()) {
        return null;
      }
      this.startActiveQuery();
    }

    if (!this.observable) {
      return null;
    }

    const result = this.observable.refetch(variables as any);
    this.retrieveCurrentResult();
    return result.then((queryResult) => {
      if (!queryResult.loading) {
        this.setQueryLoading(false);
      }
      return queryResult;
    });
  }

  refresh(variables?: OptionalVariables) {
    if (this.shouldSkip()) {
      return Promise.resolve(null);
    }
    this.stopActiveQuery();
    if (typeof variables !== "undefined") {
      this.executeApollo(variables);
    } else {
      this.startActiveQuery();
    }
    return Promise.resolve(null);
  }

  private setSkip(skip: boolean) {
    if (skip) {
      this.stopActiveQuery();
      return;
    }

    this.startActiveQuery();
  }

  private startActiveQuery() {
    if (this.active) {
      return;
    }

    this.active = true;
    this.previousVariablesKey = null;

    if (typeof this.definition.variables === "function") {
      this.variablesUnwatch = this.vm.$watch(
        () => this.getVariables(),
        (variables) => this.executeApollo(variables),
        { immediate: true, deep: this.definition.deep }
      );
    } else {
      this.executeApollo(this.getVariables());
    }
  }

  private executeApollo(variables: OptionalVariables) {
    const variablesKey = this.stringify(variables);

    if (this.observable && variablesKey === this.previousVariablesKey) {
      return;
    }

    this.previousVariablesKey = variablesKey;
    this.stopCurrentSubscription();

    const observable = apolloClient.watchQuery<any, any>({
      query: this.definition.query,
      variables: variables as any,
      fetchPolicy: this.definition.fetchPolicy ?? "cache-and-network",
      notifyOnNetworkStatusChange: this.definition.notifyOnNetworkStatusChange,
    });
    this.observable = observable;
    this.subscription = observable.subscribe({
      next: (result) => this.nextResult(result),
      error: (error) => {
        this.setQueryLoading(false);
        // eslint-disable-next-line no-console
        console.error(error);
      },
    });

    if (
      this.definition.fetchPolicy !== "no-cache" ||
      this.definition.notifyOnNetworkStatusChange
    ) {
      const currentResult = this.retrieveCurrentResult();
      if (
        currentResult &&
        (this.definition.notifyOnNetworkStatusChange || !currentResult.loading)
      ) {
        this.nextResult(currentResult);
      }
    }
  }

  private stopActiveQuery() {
    this.variablesUnwatch?.();
    this.variablesUnwatch = null;
    this.active = false;
    this.previousVariablesKey = null;
    this.stopCurrentSubscription();
    this.setQueryLoading(false);
  }

  private stopCurrentSubscription() {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.observable = null;
  }

  private retrieveCurrentResult() {
    if (!this.observable) {
      return null;
    }

    const currentResult = this.observable.getCurrentResult();
    if (currentResult.loading) {
      this.setQueryLoading(true);
    }
    return currentResult;
  }

  private nextResult(result: ApolloQueryResult<any>) {
    if (!result.loading) {
      this.setQueryLoading(false);
    }

    if (result.data == null) {
      return;
    }

    const value = this.definition.update
      ? this.definition.update.call(this.vm, result.data)
      : result.data[this.key];

    if (
      !this.definition.update &&
      typeof value === "undefined" &&
      Object.keys(result.data).length
    ) {
      // eslint-disable-next-line no-console
      console.error(`Missing ${this.key} attribute on result`, result.data);
      return;
    }

    this.vm.$set(this.hasDataField ? this.vm.$data : this.vm, this.key, value);
  }

  private shouldSkip() {
    return Boolean(this.resolveMaybeFactory(this.definition.skip));
  }

  private getVariables() {
    return this.resolveMaybeFactory(this.definition.variables);
  }

  private setQueryLoading(loading: boolean) {
    if (this.loading === loading) {
      return;
    }
    this.loading = loading;
    this.setLoading(this.key, loading);
  }

  private resolveMaybeFactory<T>(value: MaybeFactory<T> | undefined) {
    return typeof value === "function"
      ? (value as (this: any) => T).call(this.vm)
      : value;
  }

  private stringify(value: unknown) {
    return (
      JSON.stringify(value, (_key, item) =>
        item instanceof Date ? item.toISOString() : item
      ) ?? ""
    );
  }
}

class LegacySmartQueryManager {
  private readonly queries: Record<string, LegacySmartQuery>;
  private readonly loadingByQuery: Record<string, boolean> = {};

  constructor(
    private readonly vm: Vue,
    definitions: LegacySmartQueryDefinitions
  ) {
    this.queries = Object.fromEntries(
      Object.entries(definitions).map(([key, definition]) => [
        key,
        new LegacySmartQuery(vm, key, definition, this.setLoading),
      ])
    );
  }

  start() {
    Object.values(this.queries).forEach((query) => query.start());
  }

  stop() {
    Object.values(this.queries).forEach((query) => query.stop());
  }

  refetch(key: string) {
    return this.queries[key]?.refetch() ?? Promise.resolve(null);
  }

  refetchAll() {
    return Promise.all(
      Object.values(this.queries).map((query) => query.refetch())
    );
  }

  private setLoading = (key: string, loading: boolean) => {
    this.loadingByQuery[key] = loading;
    const anyLoading = Object.values(this.loadingByQuery).some(Boolean);
    this.vm.$set(this.vm, "apolloLoading", anyLoading);
  };
}

const managers = new WeakMap<Vue, LegacySmartQueryManager>();

export function legacySmartQueries(
  definitions: LegacySmartQueryDefinitions
): VueConstructor {
  return Vue.extend({
    data() {
      return {
        ...Object.fromEntries(
          Object.keys(definitions).map((key) => [key, undefined])
        ),
        apolloLoading: false,
      };
    },
    created() {
      const manager = new LegacySmartQueryManager(this, definitions);
      managers.set(this, manager);
      manager.start();
    },
    beforeDestroy() {
      managers.get(this)?.stop();
      managers.delete(this);
    },
  });
}

export function refetchLegacySmartQuery(vm: Vue, key: string) {
  return managers.get(vm)?.refetch(key) ?? Promise.resolve(null);
}

export function refetchLegacySmartQueries(vm: Vue) {
  return managers.get(vm)?.refetchAll() ?? Promise.resolve([]);
}
